import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, ActionIcon, Tabs, TextInput, Checkbox } from '@mantine/core';
import {
    IconShieldLock,
    IconChecks,
    IconX,
    IconListDetails,
    IconPlus,
    IconBuildingStore,
    IconEdit,
    IconSearch,
    IconCashEdit,
} from '@tabler/icons-react';
import { Business } from '@turizi/shared';
import { useBusinesses } from '@/hooks/api-hooks';
import { MENU_PERMISSIONS_LIST } from '@/components/left-menu';
import { CREATE_ACTION_PERMISSIONS_LIST } from '@/components/create-actions-menu';
import BusinessSelector from '@/components/business-selector';
import EditionGroupSelector from '@/components/edition-group-selector';
import { TagIcon } from '@/lib/tag-utils';

export interface UserPermissionsSelectorProps {
    permissions: Record<string, any> | null | undefined;
    onChange: (permissions: Record<string, any>) => void;
}

export function UserPermissionsSelector({ permissions, onChange }: UserPermissionsSelectorProps) {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState<string | null>('menu');
    const [showAddBusinessModal, setShowAddBusinessModal] = useState(false);
    const [searchMenuQuery, setSearchMenuQuery] = useState('');
    const [searchCreateQuery, setSearchCreateQuery] = useState('');

    const { data: businessesResponse } = useBusinesses();
    const allBusinesses = businessesResponse?.data || [];

    const currentPermissions = permissions || {};
    const businessMode = currentPermissions.reservation_business_mode || 'all';
    const editReservationMode = currentPermissions.edit_reservation_mode || 'all';
    const allowedIds: number[] = Array.isArray(currentPermissions.allowed_reservation_business_ids)
        ? currentPermissions.allowed_reservation_business_ids
        : [];

    const isAllowed = (key: string) => {
        return currentPermissions[key] !== false;
    };

    const togglePermission = (key: string) => {
        const newValue = !isAllowed(key);
        onChange({
            ...currentPermissions,
            [key]: newValue,
        });
    };

    const handleSelectAllGroup = (items: { key: string }[]) => {
        const updated: Record<string, any> = { ...currentPermissions };
        items.forEach((item) => {
            updated[item.key] = true;
        });
        onChange(updated);
    };

    const handleDeselectAllGroup = (items: { key: string }[]) => {
        const updated: Record<string, any> = { ...currentPermissions };
        items.forEach((item) => {
            updated[item.key] = false;
        });
        onChange(updated);
    };

    const handleSelectAll = () => {
        const updated: Record<string, any> = { ...currentPermissions };
        MENU_PERMISSIONS_LIST.forEach((item) => {
            updated[item.key] = true;
        });
        CREATE_ACTION_PERMISSIONS_LIST.forEach((item) => {
            updated[item.key] = true;
        });
        updated.edit_prices_and_costs_manually = true;
        onChange(updated);
    };

    const handleDeselectAll = () => {
        const updated: Record<string, any> = { ...currentPermissions };
        MENU_PERMISSIONS_LIST.forEach((item) => {
            updated[item.key] = false;
        });
        CREATE_ACTION_PERMISSIONS_LIST.forEach((item) => {
            updated[item.key] = false;
        });
        updated.edit_prices_and_costs_manually = false;
        onChange(updated);
    };

    const handleModeChange = (mode: 'all' | 'selected') => {
        onChange({
            ...currentPermissions,
            reservation_business_mode: mode,
        });
    };

    const handleEditModeChange = (mode: 'all' | 'created_only' | 'group') => {
        onChange({
            ...currentPermissions,
            edit_reservation_mode: mode,
        });
    };

    const handleAddBusiness = (business: Business) => {
        if (allowedIds.includes(business.id)) return;
        onChange({
            ...currentPermissions,
            allowed_reservation_business_ids: [...allowedIds, business.id],
        });
    };

    const handleRemoveBusiness = (businessId: number) => {
        onChange({
            ...currentPermissions,
            allowed_reservation_business_ids: allowedIds.filter((id) => id !== businessId),
        });
    };

    const totalPermissionsCount = MENU_PERMISSIONS_LIST.length + CREATE_ACTION_PERMISSIONS_LIST.length;
    const totalActiveCount =
        MENU_PERMISSIONS_LIST.filter((item) => isAllowed(item.key)).length +
        CREATE_ACTION_PERMISSIONS_LIST.filter((item) => isAllowed(item.key)).length;

    const activeMenuCount = MENU_PERMISSIONS_LIST.filter((item) => isAllowed(item.key)).length;
    const activeCreateCount = CREATE_ACTION_PERMISSIONS_LIST.filter((item) => isAllowed(item.key)).length;

    const filteredMenuItems = MENU_PERMISSIONS_LIST.filter((item) => {
        if (!searchMenuQuery.trim()) return true;
        const translated = t(item.labelKey).toLowerCase();
        return translated.includes(searchMenuQuery.toLowerCase().trim());
    });

    const filteredCreateItems = CREATE_ACTION_PERMISSIONS_LIST.filter((item) => {
        if (!searchCreateQuery.trim()) return true;
        const translated = t(item.labelKey).toLowerCase();
        return translated.includes(searchCreateQuery.toLowerCase().trim());
    });

    return (
        <div className="mt-5 border-t border-gray-200/80 pt-5 space-y-6">
            {/* Permission Overview Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-gray-50/90 p-4 rounded-2xl border border-gray-200/80">
                <div className="flex items-center gap-3">
                    <div className="p-2.5 rounded-xl bg-emerald-100 text-emerald-800 flex-shrink-0">
                        <IconShieldLock size={20} />
                    </div>
                    <div>
                        <div className="flex items-center gap-2 flex-wrap">
                            <div className="text-sm font-bold text-gray-900">
                                {t('permission_overview')}
                            </div>
                            <div className="text-[11px] px-2 py-0.5 rounded-md font-semibold bg-neutral-100 text-neutral-700 border border-neutral-200">
                                {totalActiveCount} / {totalPermissionsCount}
                            </div>
                        </div>
                        <div className="text-xs text-gray-500 font-medium">
                            {t('manage_permissions')}
                        </div>
                    </div>
                </div>

                <div className="flex gap-2 self-start sm:self-center">
                    <Button
                        size="xs"
                        variant="white"
                        color="emerald"
                        className="border border-gray-200 shadow-2xs hover:bg-emerald-50 hover:border-emerald-200"
                        leftSection={<IconChecks size={14} className="text-emerald-600" />}
                        onClick={handleSelectAll}
                    >
                        {t('select_all')}
                    </Button>
                    <Button
                        size="xs"
                        variant="white"
                        color="gray"
                        className="border border-gray-200 shadow-2xs hover:bg-gray-100"
                        leftSection={<IconX size={14} className="text-gray-500" />}
                        onClick={handleDeselectAll}
                    >
                        {t('unselect_all')}
                    </Button>
                </div>
            </div>

            {/* Tabbed Permission Lists */}
            <div className="bg-white rounded-2xl border border-gray-200/80 p-4 space-y-4 shadow-xs">
                <Tabs value={activeTab} onChange={setActiveTab} variant="outline" radius="md">
                    <Tabs.List className="border-b border-gray-200">
                        <Tabs.Tab
                            value="menu"
                            leftSection={<IconListDetails size={16} />}
                            rightSection={
                                <div className="text-[11px] px-2 py-0.5 rounded-md font-semibold bg-neutral-100 text-neutral-700 border border-neutral-200">
                                    {activeMenuCount}/{MENU_PERMISSIONS_LIST.length}
                                </div>
                            }
                            className="font-semibold text-xs py-2.5"
                        >
                            {t('menu_permissions')}
                        </Tabs.Tab>

                        <Tabs.Tab
                            value="create"
                            leftSection={<IconPlus size={16} />}
                            rightSection={
                                <div className="text-[11px] px-2 py-0.5 rounded-md font-semibold bg-neutral-100 text-neutral-700 border border-neutral-200">
                                    {activeCreateCount}/{CREATE_ACTION_PERMISSIONS_LIST.length}
                                </div>
                            }
                            className="font-semibold text-xs py-2.5"
                        >
                            {t('create_action_permissions')}
                        </Tabs.Tab>
                    </Tabs.List>

                    {/* Menu Permissions Panel */}
                    <Tabs.Panel value="menu" className="pt-4 space-y-3">
                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
                            <TextInput
                                size="xs"
                                radius="md"
                                placeholder={t('search_permissions')}
                                value={searchMenuQuery}
                                onChange={(e) => setSearchMenuQuery(e.currentTarget.value)}
                                leftSection={<IconSearch size={14} className="text-gray-400" />}
                                rightSection={
                                    searchMenuQuery ? (
                                        <ActionIcon size="xs" variant="subtle" color="gray" onClick={() => setSearchMenuQuery('')}>
                                            <IconX size={12} />
                                        </ActionIcon>
                                    ) : null
                                }
                                className="flex-1 max-w-sm"
                            />
                            <div className="flex items-center gap-2">
                                <Button
                                    size="xs"
                                    variant="subtle"
                                    color="emerald"
                                    onClick={() => handleSelectAllGroup(MENU_PERMISSIONS_LIST)}
                                >
                                    {t('select_all')}
                                </Button>
                                <Button
                                    size="xs"
                                    variant="subtle"
                                    color="gray"
                                    onClick={() => handleDeselectAllGroup(MENU_PERMISSIONS_LIST)}
                                >
                                    {t('unselect_all')}
                                </Button>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[340px] overflow-y-auto subtle-scrollbar p-0.5">
                            {filteredMenuItems.length === 0 ? (
                                <div className="col-span-full text-xs text-gray-400 text-center py-6">
                                    {t('no_results_for_search')}
                                </div>
                            ) : (
                                filteredMenuItems.map((item) => {
                                    const checked = isAllowed(item.key);
                                    return (
                                        <div
                                            key={item.key}
                                            className={`flex items-center justify-between p-3 rounded-xl text-xs transition-all cursor-pointer select-none border ${checked
                                                ? 'bg-emerald-50/90 text-emerald-950 font-semibold border-emerald-300/80 shadow-2xs'
                                                : 'bg-white hover:bg-gray-50 text-gray-700 border-gray-200/80'
                                                }`}
                                            onClick={() => togglePermission(item.key)}
                                        >
                                            <span className="pr-3 leading-relaxed">{t(item.labelKey)}</span>
                                            <Checkbox
                                                size="xs"
                                                checked={checked}
                                                color="dark"
                                                readOnly
                                                className="pointer-events-none flex-shrink-0"
                                            />
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </Tabs.Panel>

                    {/* Creation Action Permissions Panel */}
                    <Tabs.Panel value="create" className="pt-4 space-y-3">
                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
                            <TextInput
                                size="xs"
                                radius="md"
                                placeholder={t('search_permissions')}
                                value={searchCreateQuery}
                                onChange={(e) => setSearchCreateQuery(e.currentTarget.value)}
                                leftSection={<IconSearch size={14} className="text-gray-400" />}
                                rightSection={
                                    searchCreateQuery ? (
                                        <ActionIcon size="xs" variant="subtle" color="gray" onClick={() => setSearchCreateQuery('')}>
                                            <IconX size={12} />
                                        </ActionIcon>
                                    ) : null
                                }
                                className="flex-1 max-w-sm"
                            />
                            <div className="flex items-center gap-2">
                                <Button
                                    size="xs"
                                    variant="subtle"
                                    color="emerald"
                                    onClick={() => handleSelectAllGroup(CREATE_ACTION_PERMISSIONS_LIST)}
                                >
                                    {t('select_all')}
                                </Button>
                                <Button
                                    size="xs"
                                    variant="subtle"
                                    color="gray"
                                    onClick={() => handleDeselectAllGroup(CREATE_ACTION_PERMISSIONS_LIST)}
                                >
                                    {t('unselect_all')}
                                </Button>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[340px] overflow-y-auto subtle-scrollbar p-0.5">
                            {filteredCreateItems.length === 0 ? (
                                <div className="col-span-full text-xs text-gray-400 text-center py-6">
                                    {t('no_results_for_search')}
                                </div>
                            ) : (
                                filteredCreateItems.map((item) => {
                                    const checked = isAllowed(item.key);
                                    return (
                                        <div
                                            key={item.key}
                                            className={`flex items-center justify-between p-3 rounded-xl text-xs transition-all cursor-pointer select-none border ${checked
                                                ? 'bg-emerald-50/90 text-emerald-950 font-semibold border-emerald-300/80 shadow-2xs'
                                                : 'bg-white hover:bg-gray-50 text-gray-700 border-gray-200/80'
                                                }`}
                                            onClick={() => togglePermission(item.key)}
                                        >
                                            <span className="pr-3 leading-relaxed">{t(item.labelKey)}</span>
                                            <Checkbox
                                                size="xs"
                                                checked={checked}
                                                color="dark"
                                                readOnly
                                                className="pointer-events-none flex-shrink-0"
                                            />
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </Tabs.Panel>
                </Tabs>
            </div>

            {/* Reservation Business Access Section */}
            <div className="space-y-3.5 pt-4 border-t border-gray-200/70">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <IconBuildingStore size={18} className="text-gray-700 flex-shrink-0" />
                        <div>
                            <div className="text-xs font-bold text-gray-900 uppercase tracking-wider">
                                {t('reservation_business_access')}
                            </div>
                            <div className="text-xs text-gray-500 leading-relaxed">
                                {businessMode === 'all' ? t('all_businesses_desc') : t('selected_businesses_desc')}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-8 pt-1">
                    <label className="flex items-center gap-2.5 cursor-pointer select-none">
                        <Checkbox
                            size="xs"
                            color="dark"
                            checked={businessMode === 'all'}
                            onChange={() => handleModeChange('all')}
                        />
                        <span className="text-xs text-neutral-800 font-medium">
                            {t('all_businesses')}
                        </span>
                    </label>

                    <label className="flex items-center gap-2.5 cursor-pointer select-none">
                        <Checkbox
                            size="xs"
                            color="dark"
                            checked={businessMode === 'selected'}
                            onChange={() => handleModeChange('selected')}
                        />
                        <span className="text-xs text-neutral-800 font-medium">
                            {t('selected_businesses')}
                        </span>
                    </label>
                </div>

                {businessMode === 'selected' && (
                    <div className="space-y-3 p-3.5 bg-gray-50/90 rounded-2xl border border-gray-200/80">
                        <div className="flex items-center justify-between">
                            <div className="text-xs text-gray-700 font-semibold">
                                {t('select_allowed_businesses')} ({allowedIds.length})
                            </div>
                            <Button
                                size="xs"
                                variant="light"
                                color="black"
                                leftSection={<IconPlus size={14} />}
                                onClick={() => setShowAddBusinessModal(true)}
                            >
                                {t('add_business')}
                            </Button>
                        </div>

                        {allowedIds.length === 0 ? (
                            <div className="text-xs text-amber-800 bg-amber-50/90 p-3 rounded-xl border border-amber-200/80 font-medium">
                                {t('no_businesses_selected')}
                            </div>
                        ) : (
                            <div className="flex flex-wrap gap-2">
                                {allowedIds.map((id) => {
                                    const bus = allBusinesses.find((b) => b.id === id);
                                    const label = bus ? bus.name : `#${id}`;
                                    return (
                                        <div
                                            key={id}
                                            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium bg-white border border-neutral-200 text-neutral-800 shadow-2xs select-none"
                                        >
                                            <span className="flex items-center gap-1.5 py-0.5">
                                                <TagIcon name={bus?.category || 'business'} hideText size="xs" />
                                                <span>{label}</span>
                                            </span>
                                            <ActionIcon
                                                size="xs"
                                                color="gray"
                                                variant="transparent"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleRemoveBusiness(id);
                                                }}
                                            >
                                                <IconX size={12} />
                                            </ActionIcon>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}

                <BusinessSelector
                    opened={showAddBusinessModal}
                    onClose={() => setShowAddBusinessModal(false)}
                    exclude={allowedIds}
                    onSelect={(item) => {
                        handleAddBusiness(item);
                        setShowAddBusinessModal(false);
                    }}
                />
            </div>

            {/* Reservation Edit Access Section */}
            <div className="space-y-3.5 pt-4 border-t border-gray-200/70">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <IconEdit size={18} className="text-gray-700 flex-shrink-0" />
                        <div>
                            <div className="text-xs font-bold text-gray-900 uppercase tracking-wider">
                                {t('reservation_edit_access')}
                            </div>
                            <div className="text-xs text-gray-500 leading-relaxed">
                                {editReservationMode === 'all'
                                    ? t('all_reservations_desc')
                                    : editReservationMode === 'group'
                                        ? t('edition_group_reservations_desc')
                                        : t('only_created_reservations_desc')}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-8 pt-1">
                    <label className="flex items-center gap-2.5 cursor-pointer select-none">
                        <Checkbox
                            size="xs"
                            color="dark"
                            checked={editReservationMode === 'all'}
                            onChange={() => handleEditModeChange('all')}
                        />
                        <span className="text-xs text-neutral-800 font-medium">
                            {t('all_reservations')}
                        </span>
                    </label>

                    <label className="flex items-center gap-2.5 cursor-pointer select-none">
                        <Checkbox
                            size="xs"
                            color="dark"
                            checked={editReservationMode === 'created_only'}
                            onChange={() => handleEditModeChange('created_only')}
                        />
                        <span className="text-xs text-neutral-800 font-medium">
                            {t('only_created_reservations')}
                        </span>
                    </label>

                    <label className="flex items-center gap-2.5 cursor-pointer select-none">
                        <Checkbox
                            size="xs"
                            color="dark"
                            checked={editReservationMode === 'group'}
                            onChange={() => handleEditModeChange('group')}
                        />
                        <span className="text-xs text-neutral-800 font-medium">
                            {t('edition_group_reservations')}
                        </span>
                    </label>
                </div>

                {editReservationMode === 'group' && (
                    <EditionGroupSelector
                        value={currentPermissions.edition_group}
                        onChange={(group) => {
                            onChange({
                                ...currentPermissions,
                                edition_group: group,
                                edition_groups: group ? [group] : [],
                            });
                        }}
                    />
                )}
            </div>

            {/* Manual Price & Cost Permission Section */}
            <div className="space-y-3.5 pt-4 border-t border-gray-200/70">
                <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-2">
                        <IconCashEdit size={18} className="text-gray-700 flex-shrink-0" />
                        <div>
                            <div className="text-xs font-bold text-gray-900 uppercase tracking-wider">
                                {t('edit_prices_and_costs_manually')}
                            </div>
                            <div className="text-xs text-gray-500 leading-relaxed">
                                {t('edit_prices_and_costs_manually_desc')}
                            </div>
                        </div>
                    </div>
                    <Checkbox
                        size="sm"
                        color="dark"
                        checked={currentPermissions.edit_prices_and_costs_manually !== false}
                        onChange={(e) => {
                            onChange({
                                ...currentPermissions,
                                edit_prices_and_costs_manually: e.currentTarget.checked,
                            });
                        }}
                    />
                </div>
            </div>
        </div>
    );
}

export default UserPermissionsSelector;
